import { useEffect, useRef, useState, ChangeEvent, SetStateAction, Dispatch, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import sharedStateBridge from '@sendible/shared-state-bridge';
import { DateTime } from 'luxon';
import { useWindowSize, MOBILE_MAX_SIZE } from '@sendible/design-system';
import { DatePicker } from './components';
import { Container, DateInput, FilterContainer } from './index.styles';

type Props = {
  currentCampaign: CampaignsData;
  setCurrentCampaign: Dispatch<SetStateAction<CampaignsData>>;
};
export const Calendar = ({ currentCampaign, setCurrentCampaign }: Props) => {
  const { startDate, endDate } = currentCampaign;
  const { height, width } = useWindowSize();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isStartOrEndDate, setIsStartOrEndDate] = useState<IsStartOrEndDateType>('start');

  const { x, y, strategy, refs } = useFloating({
    open: calendarOpen,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (startDateRef.current && endDateRef.current) {
      startDateRef.current.value = startDate ? (startDate.toISODate() as string) : '';

      endDateRef.current.value = endDate ? (endDate.toISODate() as string) : '';
    }
  }, [startDate, endDate]);

  const updateDateWhenDateNull = () => {
    if (startDateRef.current && endDateRef.current) {
      setCurrentCampaign((prev) => {
        return {
          ...prev,
          startDate: isStartOrEndDate === 'start' ? null : startDate,
          endDate: isStartOrEndDate === 'end' ? null : endDate,
        };
      });

      if (isStartOrEndDate === 'start') {
        startDateRef.current.value = '';
        startDateRef.current.focus();
      }

      if (isStartOrEndDate === 'end') {
        endDateRef.current.value = '';
        endDateRef.current.focus();
      }
    }
  };

  const updateDateWhenStartDate = (date: DateTime) => {
    if (startDateRef.current && endDateRef.current) {
      if (endDate && date > endDate) {
        setCurrentCampaign((prev) => {
          return {
            ...prev,
            startDate: date,
            endDate: null,
          };
        });
      } else {
        setCurrentCampaign((prev) => {
          return {
            ...prev,
            startDate: date,
          };
        });
      }

      setIsStartOrEndDate('end');
      endDateRef.current.focus();
    }
  };

  const updateDateWhenEndDate = (date: DateTime) => {
    if (startDateRef.current && endDateRef.current) {
      if (startDate && date < startDate) {
        setCurrentCampaign((prev) => {
          return {
            ...prev,
            startDate: date,
            endDate: null,
          };
        });

        endDateRef.current.focus();
      } else {
        setCurrentCampaign((prev) => {
          return {
            ...prev,
            endDate: date,
          };
        });

        setCalendarOpen(false);
        endDateRef.current.blur();
      }
    }
  };

  const updateDate = (date: CampaignsDateTime) => {
    if (!date) {
      updateDateWhenDateNull();

      return;
    }

    if (isStartOrEndDate === 'start') {
      updateDateWhenStartDate(date);
    }

    if (isStartOrEndDate === 'end') {
      updateDateWhenEndDate(date);
    }
  };

  const handleDateInputOnInput = (event: FormEvent<HTMLInputElement>) => {
    const date = event.currentTarget.value;

    if (date.length < 1) {
      updateDate(null);
    }
  };

  const handleDateInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const onChangeDate = event.currentTarget.value;
    const date = DateTime.fromISO(onChangeDate);

    if (date.isValid && date > DateTime.fromISO('2000-01-01')) {
      updateDate(date);
    }
  };

  const openCalendar = (target: IsStartOrEndDateType) => {
    setIsStartOrEndDate(target);
    setCalendarOpen(true);
  };

  useEffect(() => {
    if (!calendarOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);

      if (!inContainer && !inPanel) {
        setCalendarOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [calendarOpen]);

  const inputComponent = (
    <div
      ref={(node) => {
        containerRef.current = node;
        refs.setReference(node);
      }}
    >
      <FilterContainer>
        <DateInput
          type="date"
          ref={startDateRef}
          onFocus={() => openCalendar('start')}
          onClick={() => openCalendar('start')}
          onInput={handleDateInputOnInput}
          onChange={handleDateInputOnChange}
        />
        <DateInput
          type="date"
          ref={endDateRef}
          onFocus={() => openCalendar('end')}
          onClick={() => openCalendar('end')}
          onInput={handleDateInputOnInput}
          onChange={handleDateInputOnChange}
        />
      </FilterContainer>
    </div>
  );

  const datePickerComponent = (
    <div
      ref={(node) => {
        refs.setFloating(node);
        panelRef.current = node;
      }}
      style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
    >
      <DatePicker
        key={`${isStartOrEndDate}-${startDate?.toISODate() ?? 'none'}-${endDate?.toISODate() ?? 'none'}`}
        selectedDateRanges={{ startDate, endDate }}
        isStartOrEndDate={isStartOrEndDate}
        dateSelectedCallback={updateDate}
        weekStart={sharedStateBridge.getSharedState()?.user?.weekStart}
        closeHandler={() => {
          setCalendarOpen(false);
        }}
      />
    </div>
  );

  const dropdownRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

  return (
    <Container>
      {width >= MOBILE_MAX_SIZE && height >= 500 ? (
        <>
          {inputComponent}
          {calendarOpen && dropdownRoot && createPortal(datePickerComponent, dropdownRoot)}
        </>
      ) : (
        <>
          {inputComponent}
          {calendarOpen && datePickerComponent}
        </>
      )}
    </Container>
  );
};
